import { useState, useCallback } from 'react';
import { createContainer } from 'unstated-next';
import { useDataProvider, useGetOne } from 'react-admin';
import {
  RegionDto,
  SupplierDto,
  SupplierFullCreateDto,
  SupplierFullDto,
  SupplierFullUpdateDto,
  SupplierRegionDto,
  SupplierRepresentativeDto,
  UserDto,
} from '@vatos-pas/common';
import { axiosInstance } from 'services/http';
import { extractErrorMessage } from 'utils';

/**
 * Adapter between the data layer and supplier components — components stay
 * data-layer agnostic so the underlying client (react-admin, react-query,
 * Apollo, fetch) can be swapped without touching them.
 *
 * `useGetOne` provides SWR semantics via the react-admin Redux cache: cached
 * data returns immediately, refetch happens in the background, and the loading
 * spinner only shows on first load when no cached data exists.
 */

export interface RegionOption {
  id: string;
  name: string;
}

export interface UserRepresentativeOption {
  id: string;
  name: string;
}

export interface SupplierFormValues {
  name: string;
  externalId: string;
  description: string;
  email: string;
  phone: string;
  contactInfo: string;
  selectedRegions: string[];
  selectedRepresentatives: string[];
}

function useSupplier() {
  const dataProvider = useDataProvider();

  const [supplierId, setSupplierId] = useState<string>('');
  const {
    data: selectedSupplier,
    loading: supplierLoading,
    loaded: supplierLoaded,
    error: supplierError,
  } = useGetOne<SupplierDto>('supplier', supplierId, {
    enabled: !!supplierId,
  });

  const isLoading = !!supplierId && !selectedSupplier && supplierLoading;
  const isRevalidating = !!supplierId && supplierLoading;

  const [formLoading, setFormLoading] = useState(false);
  const [supplierRegions, setSupplierRegions] = useState<SupplierRegionDto[]>(
    [],
  );
  const [supplierRepresentatives, setSupplierRepresentatives] = useState<
    SupplierRepresentativeDto[]
  >([]);
  const [supplierRegionsLoaded, setSupplierRegionsLoaded] = useState(false);
  const [supplierRepresentativesLoaded, setSupplierRepresentativesLoaded] =
    useState(false);
  const [regionsError, setRegionsError] = useState<string | null>(null);
  const [representativesError, setRepresentativesError] = useState<
    string | null
  >(null);

  const loadSupplierById = useCallback((id: string): void => {
    setSupplierId(id);
  }, []);

  const toSupplierFields = (values: SupplierFormValues) => ({
    name: values.name,
    externalId: values.externalId,
    description: values.description,
    email: values.email,
    phone: values.phone,
    contactInfo: values.contactInfo,
  });

  /**
   * One request: POST /supplier/full persists the supplier, its regions and
   * its representatives in a single API transaction (the old POST supplier →
   * N POST supplier-region chain left half-linked suppliers behind).
   */
  const createSupplier = useCallback(
    async (values: SupplierFormValues): Promise<SupplierFullDto> => {
      const payload: SupplierFullCreateDto = {
        supplier: toSupplierFields(values),
        regionIds: values.selectedRegions,
        representativeUserIds: values.selectedRepresentatives,
      };
      const result = await axiosInstance.post<SupplierFullDto>(
        'supplier/full',
        payload,
      );
      setSupplierId(result.data.id);
      return result.data;
    },
    [],
  );

  /**
   * One request: PATCH /supplier/:id/full with the editable fields and the
   * desired region and representative sets; the API diffs them server-side.
   */
  const updateSupplier = useCallback(
    async (
      id: string,
      values: SupplierFormValues,
    ): Promise<SupplierFullDto> => {
      if (!selectedSupplier) {
        throw new Error('Cannot update: supplier data not loaded');
      }
      const payload: SupplierFullUpdateDto = {
        supplier: toSupplierFields(values),
        regionIds: values.selectedRegions,
        representativeUserIds: values.selectedRepresentatives,
      };
      const result = await axiosInstance.patch<SupplierFullDto>(
        `supplier/${id}/full`,
        payload,
      );
      return result.data;
    },
    [selectedSupplier],
  );

  const loadRegionOptions = useCallback(
    async (_searchTerm: string): Promise<RegionOption[]> => {
      try {
        const result = await dataProvider.getList<RegionDto>('region', {
          filter: {},
          pagination: { page: 1, perPage: 999 },
          sort: { field: 'name', order: 'ASC' },
        });
        setRegionsError(null);
        return result.data.map(region => ({
          id: region.id,
          name: region.name,
        }));
      } catch (error) {
        console.error('Failed to load regions:', error);
        setRegionsError(extractErrorMessage(error, 'Failed to load regions'));
        return [];
      }
    },
    [dataProvider],
  );

  const loadUserRepresentativeOptions = useCallback(
    async (searchTerm: string): Promise<UserRepresentativeOption[]> => {
      try {
        const result = await dataProvider.getList<UserDto>(
          'user-representative',
          {
            filter: searchTerm
              ? {
                  s: {
                    $or: [
                      { firstName: { $cont: searchTerm } },
                      { lastName: { $cont: searchTerm } },
                    ],
                  },
                }
              : {},
            pagination: { page: 1, perPage: 80 },
            sort: { field: 'firstName', order: 'ASC' },
          },
        );
        setRepresentativesError(null);
        return result.data.map(user => ({
          id: user.id,
          name: `${user.firstName} ${user.lastName}`,
        }));
      } catch (error) {
        console.error('Failed to load user representatives:', error);
        setRepresentativesError(
          extractErrorMessage(error, 'Failed to load supplier representatives'),
        );
        return [];
      }
    },
    [dataProvider],
  );

  const loadSupplierRegionsForSupplier = useCallback(
    async (supplierId: string): Promise<SupplierRegionDto[]> => {
      try {
        const result = await dataProvider.getList<SupplierRegionDto>(
          'supplier-region',
          {
            filter: { supplierId },
            pagination: { page: 1, perPage: 999 },
            sort: { field: 'id', order: 'DESC' },
          },
        );
        setSupplierRegions(result.data);
        setSupplierRegionsLoaded(true);
        setRegionsError(null);
        return result.data;
      } catch (error) {
        console.error('Failed to load supplier regions:', error);
        setSupplierRegions([]);
        setRegionsError(
          extractErrorMessage(error, 'Failed to load supplier regions'),
        );
        return [];
      }
    },
    [dataProvider],
  );

  const loadSupplierRepresentativesForSupplier = useCallback(
    async (supplierId: string): Promise<SupplierRepresentativeDto[]> => {
      try {
        const result = await dataProvider.getList<SupplierRepresentativeDto>(
          'supplier-representative',
          {
            filter: { supplierId },
            pagination: { page: 1, perPage: 999 },
            sort: { field: 'id', order: 'DESC' },
          },
        );
        setSupplierRepresentatives(result.data);
        setSupplierRepresentativesLoaded(true);
        setRepresentativesError(null);
        return result.data;
      } catch (error) {
        console.error('Failed to load supplier representatives:', error);
        setSupplierRepresentatives([]);
        setRepresentativesError(
          extractErrorMessage(error, 'Failed to load supplier representatives'),
        );
        return [];
      }
    },
    [dataProvider],
  );

  const resetLoadedFlags = useCallback(() => {
    setSupplierRegionsLoaded(false);
    setSupplierRepresentativesLoaded(false);
  }, []);

  return {
    selectedSupplier,
    isLoading,
    isRevalidating,
    supplierLoaded,
    supplierError,
    formLoading,
    supplierRegions,
    supplierRepresentatives,
    supplierRegionsLoaded,
    supplierRepresentativesLoaded,
    regionsError,
    representativesError,
    loadSupplierById,
    createSupplier,
    updateSupplier,
    loadRegionOptions,
    loadUserRepresentativeOptions,
    loadSupplierRegionsForSupplier,
    loadSupplierRepresentativesForSupplier,
    setFormLoading,
    resetLoadedFlags,
  };
}

export const SupplierContainer = createContainer(useSupplier);
